/**
 * src/services/spatial/spatialEnv.ts
 *
 * Centralised environment-variable reader for the GeoLink spatial-objects
 * subsystem.
 *
 * The user-facing spec (the "Implement GeoTrust map rendering and interaction
 * support for GeoLink spatial objects" task) lists the env keys with the
 * `VITE_*` prefix. The current GeoTrust frontend, however, is a Create-React-App
 * project; CRA only inlines variables prefixed with `REACT_APP_*` at build
 * time. To stay forward-compatible (a future Vite migration is a stated
 * goal of the spec) AND to actually function on the current CRA build,
 * this module reads BOTH prefixes for every key, with the following
 * priority:
 *
 *   1. `import.meta.env.VITE_*`           — when running under Vite
 *   2. `process.env.VITE_*`               — bundler-injected fallback
 *   3. `process.env.REACT_APP_*`          — current CRA prefix (primary today)
 *   4. window.__GEOTRUST_SPATIAL_ENV__    — runtime override for tests
 *   5. Hardcoded fallback (only for non-secret values)
 *
 * Secrets (Mapbox token, MapTiler key) NEVER fall back to a hardcoded
 * value — if they're not configured we return `null` and the provider
 * factory will refuse to construct that provider.
 *
 * GeoLink API keys are deliberately absent from this module. Browser traffic
 * goes through the GeoTrust backend proxy (`getGeoLinkApiBaseUrl()`), which
 * injects the server-side key; adding a `REACT_APP_GEOLINK_*_KEY` here would
 * inline that credential into the JS bundle for anyone to read.
 */

export type MapProviderId = 'mapbox' | 'maptiler' | 'maplibre';

/**
 * Window-level override hook used by the dev test mode and unit tests
 * to swap providers/keys without rebuilding. Anything set here takes
 * priority over `process.env` so tests can stub.
 */
declare global {
  interface Window {
    __GEOTRUST_SPATIAL_ENV__?: Partial<Record<string, string>>;
  }
}

/**
 * Static REACT_APP_* table.
 *
 * IMPORTANT: every entry MUST use a literal `process.env.REACT_APP_*`
 * member-expression. CRA's webpack `DefinePlugin` only inlines values
 * when it sees that exact literal pattern at build time; dynamic
 * lookups like `process.env[key]` are not always rewritten and can
 * silently come back undefined in some webpack/CRA setups (the
 * symptom we hit when the MapTiler key looked correct but
 * `getMapTilerKey()` still returned null at runtime).
 *
 * If you add a new spatial-subsystem env var, add it here too.
 */
const STATIC_REACT_APP_ENV: Record<string, string | undefined> = {
  REACT_APP_MAP_PROVIDER:                process.env.REACT_APP_MAP_PROVIDER,
  REACT_APP_MAPBOX_TOKEN:                process.env.REACT_APP_MAPBOX_TOKEN,
  REACT_APP_MAPTILER_KEY:                process.env.REACT_APP_MAPTILER_KEY,
  REACT_APP_SPATIAL_RENDER_PROVIDER:     process.env.REACT_APP_SPATIAL_RENDER_PROVIDER,
  REACT_APP_SPATIAL_DECK_RENDERER:       process.env.REACT_APP_SPATIAL_DECK_RENDERER,
  REACT_APP_SPATIAL_DECK_INTERLEAVED:    process.env.REACT_APP_SPATIAL_DECK_INTERLEAVED,
  REACT_APP_MAP_API_ENABLED:             process.env.REACT_APP_MAP_API_ENABLED,
  REACT_APP_MAP_API_BASE_URL:            process.env.REACT_APP_MAP_API_BASE_URL,
  REACT_APP_MAP_DELTA_ENABLED:           process.env.REACT_APP_MAP_DELTA_ENABLED,
  REACT_APP_MAP_DELTA_WS_PATH:           process.env.REACT_APP_MAP_DELTA_WS_PATH,
  REACT_APP_MAPTILER_OVERLAY_ENABLED:    process.env.REACT_APP_MAPTILER_OVERLAY_ENABLED,
  REACT_APP_MAPTILER_OVERLAY_STYLE:      process.env.REACT_APP_MAPTILER_OVERLAY_STYLE,
  REACT_APP_MAPTILER_OVERLAY_OPACITY:    process.env.REACT_APP_MAPTILER_OVERLAY_OPACITY,
  REACT_APP_GEOLINK_API_BASE_URL:        process.env.REACT_APP_GEOLINK_API_BASE_URL,
  REACT_APP_GEOLINK_API_URL:             process.env.REACT_APP_GEOLINK_API_URL,
};

/**
 * Read an env value by Vite-style key name (e.g. `VITE_MAPTILER_KEY`).
 *
 * Resolution order:
 *   1. `__VITE_ENV__` shim (future Vite migration / tests).
 *   2. STATIC_REACT_APP_ENV — CRA's DefinePlugin-injected REACT_APP_* values.
 *   3. window.__GEOTRUST_SPATIAL_ENV__ — runtime override hook.
 */
function readEnvKey(key: string): string | null {
  // 1. Vite-style import.meta.env (only present when bundled by Vite).
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const im: any = (globalThis as any).__VITE_ENV__;
    if (im && typeof im[key] === 'string' && im[key].length > 0) {
      return im[key] as string;
    }
  } catch {
    // ignore — `import.meta` not available under CRA
  }

  // 2. CRA REACT_APP_* fallback via the static table above.
  const reactKey = key.startsWith('VITE_') ? `REACT_APP_${key.slice('VITE_'.length)}` : key;
  const fromStatic = STATIC_REACT_APP_ENV[reactKey];
  if (typeof fromStatic === 'string' && fromStatic.length > 0) {
    return fromStatic;
  }

  // 3. Window-level runtime override (tests / dev tools / quick toggles).
  if (typeof window !== 'undefined' && window.__GEOTRUST_SPATIAL_ENV__) {
    const w = window.__GEOTRUST_SPATIAL_ENV__[key];
    if (typeof w === 'string' && w.length > 0) return w;
    const wf = window.__GEOTRUST_SPATIAL_ENV__[reactKey];
    if (typeof wf === 'string' && wf.length > 0) return wf;
  }

  return null;
}

/**
 * Diagnostic helper — returns the inlined values of every spatial
 * subsystem env var so DevTools / the controls UI can show whether
 * the bundle actually got the key. Useful for quickly distinguishing
 * "I forgot to restart npm start" from "my key is genuinely null".
 *
 * Truncates secrets to first 4 characters so the snapshot is safe to
 * console.log / paste into a screenshot.
 */
export function debugSpatialEnvSnapshot(): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(STATIC_REACT_APP_ENV)) {
    if (typeof v !== 'string' || v.length === 0) {
      out[k] = '<not set>';
    } else if (k.includes('KEY') || k.includes('TOKEN') || k.includes('SECRET')) {
      out[k] = `${v.slice(0, 4)}…(${v.length} chars)`;
    } else {
      out[k] = v;
    }
  }
  return out;
}

// Hoist a one-off debug function onto `window` so devs can paste
// `window.__geotrust_spatialEnv()` into the browser console to see
// which env vars actually made it into the bundle. No-op under SSR.
if (typeof window !== 'undefined') {
  (window as unknown as { __geotrust_spatialEnv?: () => Record<string, string | undefined> })
    .__geotrust_spatialEnv = debugSpatialEnvSnapshot;
}

/** Configured map provider (defaults to mapbox to preserve existing behaviour). */
export function getConfiguredProvider(): MapProviderId {
  const raw = (readEnvKey('VITE_MAP_PROVIDER') || 'mapbox').toLowerCase();
  if (raw === 'maptiler' || raw === 'maplibre' || raw === 'mapbox') return raw;
  // Unknown values silently fall back to mapbox so we never break the
  // existing map at runtime if someone typos the env variable.
  // console.warn(`[spatialEnv] Unknown VITE_MAP_PROVIDER "${raw}", falling back to "mapbox"`);
  return 'mapbox';
}

/** Mapbox public token (required for VITE_MAP_PROVIDER=mapbox). */
export function getMapboxToken(): string | null {
  return readEnvKey('VITE_MAPBOX_TOKEN');
}

/** MapTiler API key (required for VITE_MAP_PROVIDER=maptiler). */
export function getMapTilerKey(): string | null {
  return readEnvKey('VITE_MAPTILER_KEY');
}

/**
 * Which provider hint to send to GeoLink's
 * `GET /api/spatial-objects/render?provider=...` endpoint.
 *
 * MapTiler's render mode returns a SUPERSET of property aliases
 * (`render_height`, `render_min_height`, `height`, `base_height`,
 * `fill_color`) that work for Mapbox rendering AND give us MapTiler
 * extras when the MapTiler overlay is enabled. So even when Mapbox is
 * the basemap, we may legitimately want to request the maptiler
 * provider mode — that's why this is a separate setting from
 * `VITE_MAP_PROVIDER`.
 *
 * When unset we fall back to the configured basemap provider so the
 * default behaviour is "request what your basemap is" — this matches
 * the integration guide's implicit expectation.
 */
export function getSpatialRenderProvider(): 'generic' | 'mapbox' | 'maplibre' | 'maptiler' {
  const raw = (readEnvKey('VITE_SPATIAL_RENDER_PROVIDER') || '').toLowerCase();
  if (raw === 'generic' || raw === 'mapbox' || raw === 'maplibre' || raw === 'maptiler') {
    return raw;
  }
  // Fall back to whichever basemap provider is configured.
  const basemap = getConfiguredProvider();
  return basemap;
}

/**
 * Whether to render GeoTrust spatial objects through the deck.gl
 * layer (the new centralised application-visualization path) instead
 * of the legacy native `fill-extrusion` layers.
 *
 * Defaults to OFF so existing deployments keep the native rendering
 * until the deck path is validated per-environment. Enable with
 * `REACT_APP_SPATIAL_DECK_RENDERER=true` (or the VITE_ equivalent).
 */
export function isSpatialDeckRendererEnabled(): boolean {
  const raw = (readEnvKey('VITE_SPATIAL_DECK_RENDERER') || '').toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'yes';
}

/**
 * Whether the deck.gl overlay should interleave with the basemap's own
 * 3D content. Requires WebGL2 — the overlay downgrades to overlaid mode
 * automatically when unavailable.
 *
 * Defaults to ON: interleaved rendering shares Mapbox's exact projection
 * matrix, so deck extrusions stay locked to the basemap footprint AND
 * track the globe projection. Overlaid mode (the old default) syncs only a
 * plain Web-Mercator viewport and visibly drifts from the globe-rendered
 * basemap — that was the "building sits slightly off the footprint" bug.
 * Set VITE_SPATIAL_DECK_INTERLEAVED / REACT_APP_SPATIAL_DECK_INTERLEAVED to
 * "false" to force the legacy overlaid mode.
 */
export function isSpatialDeckInterleaved(): boolean {
  const raw = (readEnvKey('VITE_SPATIAL_DECK_INTERLEAVED') || '').toLowerCase();
  if (raw === 'false' || raw === '0' || raw === 'no') return false;
  return true;
}

/**
 * Whether GeoTrust reads spatial/map data through GeoLink's normalized
 * `/api/map/*` API (Phase 2) instead of the legacy `/api/spatial-objects
 * /render` envelope. Defaults OFF so the render path stays authoritative
 * until the map-API cutover is validated per environment.
 * Enable with `REACT_APP_MAP_API_ENABLED=true`.
 */
export function isMapApiEnabled(): boolean {
  const raw = (readEnvKey('VITE_MAP_API_ENABLED') || '').toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'yes';
}

/**
 * Base URL for GeoLink's normalized `/api/map/*` API as seen by the browser.
 *
 * The browser NEVER talks to GeoLink directly and NEVER holds the map key —
 * it calls the GeoTrust backend proxy at `/api/v2/geolink/map`, which injects
 * the server-side `GEOLINK_MAP_API_KEY` and forwards to `GEOLINK_MAP_BASE_URL`.
 * Client request paths are therefore relative (`/spatial-objects`, …), NOT the
 * upstream `/api/map/...` paths.
 *
 * An explicit `VITE_MAP_API_BASE_URL` (or `REACT_APP_MAP_API_BASE_URL`) is only
 * for SSR/Node/tests that bypass the proxy.
 */
export function getMapApiBaseUrl(): string {
  const explicit = readEnvKey('VITE_MAP_API_BASE_URL');
  if (explicit) return explicit.replace(/\/+$/, '');
  if (typeof window !== 'undefined') {
    // Browser → GeoTrust map proxy (keeps the GeoLink key server-side).
    return '/api/v2/geolink/map';
  }
  // SSR/Node fallback: hit the map API through the generic proxy path.
  return '/api/v2/geolink/map';
}

/**
 * Whether the Phase 2 aggregate map layers (country policy, execution
 * rules, presence density, agents, drones) fetch + render. These are
 * independent of the spatial-objects render pipeline — they always draw
 * through the deck.gl overlay from the normalized `/api/map/*` API.
 *
 * Defaults **ON**. Fetching is still cost-bounded: only layers the user
 * has toggled visible are requested, and only on camera-idle (plus a slow
 * safety interval). Disable entirely with `REACT_APP_MAP_LAYERS_ENABLED=false`.
 */
export function isMapLayersEnabled(): boolean {
  const raw = (readEnvKey('VITE_MAP_LAYERS_ENABLED') || '').toLowerCase();
  // Default ON: only an explicit false/0/no disables it.
  return !(raw === 'false' || raw === '0' || raw === 'no');
}

/**
 * Slow safety-refresh interval (ms) for the aggregate map layers. Live
 * updates arrive over the WS delta feed; this interval only backstops a
 * missed delta / dropped socket. Camera-idle drives the primary refresh.
 * Override with `REACT_APP_MAP_LAYERS_REFRESH_MS`.
 */
export function getMapLayersRefreshMs(): number {
  const raw = readEnvKey('VITE_MAP_LAYERS_REFRESH_MS');
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= 5_000 ? n : 60_000;
}

/**
 * Whether to open the GeoLink map delta WebSocket (`/ws/map`) for live
 * layers (rule-events, agents, live-sessions, presence, drones,
 * activity). Defaults OFF. Enable with `REACT_APP_MAP_DELTA_ENABLED=true`.
 */
export function isMapDeltaEnabled(): boolean {
  const raw = (readEnvKey('VITE_MAP_DELTA_ENABLED') || '').toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'yes';
}

/**
 * Path of the GeoTrust hub WebSocket that bridges to GeoLink's map delta
 * channels (mirrors `/ws/agents`). Browser connects here; the backend
 * hub holds GeoLink WS credentials + enforces channel authorization.
 */
export function getMapDeltaWsPath(): string {
  return readEnvKey('VITE_MAP_DELTA_WS_PATH') || '/ws/map';
}

/**
 * Whether to render a MapTiler raster tile overlay (e.g. satellite or
 * topographic imagery) on top of the Mapbox basemap. The overlay is a
 * pure tile addition — it never replaces the Mapbox map — so callers
 * keep every Mapbox feature (3D buildings, agents, geofences, …) and
 * just gain MapTiler imagery underneath / alongside.
 *
 * Always returns false when `VITE_MAPTILER_KEY` isn't configured (no
 * key → no tiles to fetch).
 */
export function isMapTilerOverlayEnabled(): boolean {
  if (!getMapTilerKey()) return false;
  const raw = (readEnvKey('VITE_MAPTILER_OVERLAY_ENABLED') || '').toLowerCase();
  return raw === 'true' || raw === '1' || raw === 'yes';
}

/** MapTiler style id used for the overlay (e.g. "satellite-v2"). */
export function getMapTilerOverlayStyle(): string {
  return readEnvKey('VITE_MAPTILER_OVERLAY_STYLE') || 'satellite-v2';
}

/** MapTiler overlay opacity 0..1. */
export function getMapTilerOverlayOpacity(): number {
  const raw = readEnvKey('VITE_MAPTILER_OVERLAY_OPACITY');
  if (!raw) return 0.55;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0.55;
  return Math.max(0, Math.min(1, n));
}

/**
 * Base URL for the GeoLink spatial-objects API. Falls back to the
 * GeoTrust backend proxy in browser contexts (which forwards to
 * GeoLink server-side) or directly to the GeoLink testnet host in
 * SSR / Node contexts. This mirrors the convention used by
 * `services/geolinkApi.ts`.
 */
export function getGeoLinkApiBaseUrl(): string {
  const explicit = readEnvKey('VITE_GEOLINK_API_BASE_URL');
  if (explicit) return explicit.replace(/\/+$/, '');

  if (typeof window !== 'undefined') {
    // Browser: route through GeoTrust backend proxy to avoid CORS,
    // matching services/geolinkApi.ts.
    return '/api/v2/geolink/proxy';
  }
  return readEnvKey('VITE_GEOLINK_API_URL') || 'https://testnet.stellargeolink.com';
}
